import { useMutation, useQuery } from '@apollo/client';
import type { AdminTatAddonLeadTimesEditor_QueryQuery } from '@orthly/graphql-inline-react';
import { graphql } from '@orthly/graphql-inline-react';
import { MUITable } from '@orthly/mui-table';
import { TimeUtils } from '@orthly/runtime-utils';
import { QuickForm, RootActionDialog, useRootActionCommand } from '@orthly/ui';
import { Grid, Icon, IconButton } from '@orthly/ui-primitives';
import _ from 'lodash';
import React from 'react';

const AdminTatAddonLeadTimesEditor_Query = graphql(`
    query AdminTatAddonLeadTimesEditor_Query {
        tatListAddOnLeadTimes {
            id
            leadTimeByType {
                buffer
                design
                fabrication
                orderReview
                shipping
                waxupReview
            }
        }
    }
`);
type AddonLeadTime = AdminTatAddonLeadTimesEditor_QueryQuery['tatListAddOnLeadTimes'][number];

const AdminAddonLeadTimeModalUpsert_Mutation = graphql(`
    mutation AdminAddonLeadTimeModalUpsert_Mutation($id: String!, $leadTimeByType: TatLeadTimeByTypeArgsDto!) {
        tatUpsertAddOnLeadTime(id: $id, leadTimeByType: $leadTimeByType) {
            id
        }
    }
`);

interface AddonLeadTimeModalProps {
    leadTime: AddonLeadTime;
    refetch: () => Promise<any>;
}

interface AddonLeadTimeModalFormVars {
    leadTimeByType: {
        design: number;
        fabrication: number;
        shipping: number;
        buffer: number;
    };
}

const AddonLeadTimeModal: React.VFC<AddonLeadTimeModalProps> = ({ leadTime, refetch }) => {
    const [open, setOpen] = React.useState<boolean>(false);

    const upsertMtn = useMutation(AdminAddonLeadTimeModalUpsert_Mutation);
    const { submit, submitting } = useRootActionCommand(upsertMtn, {
        successMessage: 'Updated addon lead time.',
        onSuccess: async () => {
            await refetch();
            setOpen(false);
        },
    });

    return (
        <RootActionDialog
            open={open}
            setOpen={setOpen}
            title={`Update TAT Addon Lead Time`}
            loading={submitting}
            content={
                <QuickForm<AddonLeadTimeModalFormVars>
                    fields={{
                        leadTimeByType: {
                            type: 'nested',
                            optional: false,
                            label: 'Lead Times',
                            fields: {
                                design: {
                                    type: 'number',
                                    label: 'Design Lead Time (Days)',
                                    optional: false,
                                },
                                fabrication: {
                                    type: 'number',
                                    label: 'Fabrication Lead Time (Days)',
                                    optional: false,
                                },
                                shipping: {
                                    type: 'number',
                                    label: 'Shipping Lead Time (Days)',
                                    optional: false,
                                },
                                buffer: {
                                    type: 'number',
                                    label: 'Buffer Lead Time (Days)',
                                    optional: false,
                                },
                            },
                        },
                    }}
                    resetOnInitialValueChange={true}
                    initialValues={{
                        leadTimeByType: {
                            buffer: TimeUtils.convertHoursToDays(leadTime.leadTimeByType.buffer, 'trunc'),
                            design: TimeUtils.convertHoursToDays(leadTime.leadTimeByType.design, 'trunc'),
                            fabrication: TimeUtils.convertHoursToDays(leadTime.leadTimeByType.fabrication, 'trunc'),
                            shipping: TimeUtils.convertHoursToDays(leadTime.leadTimeByType.shipping, 'trunc'),
                        },
                    }}
                    disabled={submitting}
                    onSubmit={async data => {
                        await submit({
                            id: leadTime.id,
                            leadTimeByType: {
                                buffer: TimeUtils.convertDaysToHours(data.leadTimeByType.buffer),
                                design: TimeUtils.convertDaysToHours(data.leadTimeByType.design),
                                fabrication: TimeUtils.convertDaysToHours(data.leadTimeByType.fabrication),
                                shipping: TimeUtils.convertDaysToHours(data.leadTimeByType.shipping),
                                orderReview: leadTime.leadTimeByType.orderReview,
                                waxupReview: leadTime.leadTimeByType.waxupReview,
                            },
                        });
                    }}
                />
            }
            CustomButton={() => (
                <IconButton onClick={() => setOpen(true)}>
                    <Icon icon={'SettingsIcon'} />
                </IconButton>
            )}
        />
    );
};

export const TatAddonLeadTimesEditor: React.VFC = () => {
    const { data: itemsData, loading, refetch } = useQuery(AdminTatAddonLeadTimesEditor_Query);
    const items = _.sortBy(itemsData?.tatListAddOnLeadTimes ?? [], r => r.id);

    return (
        <Grid container>
            <MUITable<AddonLeadTime>
                title={'Addon Lead Times'}
                loading={loading}
                data={items}
                displayOptions={{
                    fixedSearch: true,
                    elevation: 0,
                    viewColumns: true,
                    filter: true,
                    sort: true,
                }}
                actions={{
                    global: [{ icon: 'refresh', position: 'toolbar', onClick: refetch }],
                }}
                rowOptions={{ rowHover: true }}
                eventHooks={{ onRowClick: (row, actions) => actions.toggleDetailPanel(row) }}
                columns={[
                    { name: 'ID', render: r => r.id },
                    {
                        name: 'Design Lead Time (Days)',
                        title: 'Design Lead Time (Days)',
                        render: r => TimeUtils.convertHoursToDays(r.leadTimeByType.design, 'trunc'),
                    },
                    {
                        name: 'Fabrication Lead Time (Days)',
                        title: 'Fabrication Lead Time (Days)',
                        render: r => TimeUtils.convertHoursToDays(r.leadTimeByType.fabrication, 'trunc'),
                    },
                    {
                        name: 'Shipping Lead Time (Days)',
                        title: 'Shipping Lead Time (Days)',
                        render: r => TimeUtils.convertHoursToDays(r.leadTimeByType.shipping, 'trunc'),
                    },
                    {
                        name: 'Buffer Lead Time (Days)',
                        title: 'Buffer Lead Time (Days)',
                        render: r => TimeUtils.convertHoursToDays(r.leadTimeByType.buffer, 'trunc'),
                    },
                    {
                        name: `Actions`,
                        render: row => <AddonLeadTimeModal leadTime={row} refetch={refetch} key={row.id} />,
                    },
                ]}
            />
        </Grid>
    );
};
